import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getFieldSelection, insertTextIntoField, setFieldText, wrapFieldSelection } from 'text-field-edit';
import { useAuthUser } from '@/auth.ts';
import { AuthPageShell, Button, StatusBadge, statusLabel, AuthImage } from '@/components';
import { RrnDisplay } from '@/components/RrnDisplay.tsx';
import { EmailDisplay } from '@/components/EmailDisplay.tsx';
import { CameraIcon, CheckIcon, ChevronLeftIcon, ArrowRightIcon, InfoCircleIcon, MailIcon, DownloadIcon, FileDocumentIcon, BoldIcon, LinkIcon, ListBulletIcon, UploadIcon, SendIcon } from '@/components/icons.tsx';
import { useAuthFetch, usePublicOrganization } from '@/hooks';
import { formatTime, formatDateSplitter, getInitialsFromName, birthDateFromRRN, buildMedirisPatientSearchHref, resizeImageIfNeeded, convertHeicIfNeeded, renderRichText, richTextToPlainText, inactiveClosesOnLabel, triggerDownload } from '@/utils';
import type { ConversationDetail, ConversationSummary, SendMessageRequest, UploadUrlResponse, ImageFile, CreatePaymentCheckoutResponse, ExportConversationResponse } from '@dermalink/shared';
import { ALLOWED_IMAGE_TYPES, sexFromRRN } from '@dermalink/shared';
import { ROUTES, practiceConversationPath, practiceIntakePath, practicePath } from '../routes.ts';
import mediportalUrl from '../assets/mediportal.svg';
import styles from './ConversationThread.module.css';

const MAX_FILES      = 5;
const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB
const COMPOSE_MAX_HEIGHT = 130; // px — roughly 5 lines before the textarea scrolls internally


function ageFromIso(iso: string): number {
  const birth = new Date(iso);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

function doctorDisplayShortName(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'Dr. Onbekend';
  const firstInitial = parts[0][0]?.toUpperCase() ?? '';
  const last = parts[parts.length - 1];
  return `Dr. ${firstInitial}. ${last}`;
}

function doctorDisplayLastNameLabel(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'De dokter';
  return `Dokter ${parts[parts.length - 1]}`;
}

/** Extra label after "Consult". */
function consultKindTabSuffix(conv: Pick<ConversationSummary, 'consult_type' | 'intake_prescription_only'>): string {
  if (conv.consult_type === 'follow_up') return 'OPVOLG';
  if (conv.intake_prescription_only === true) return 'VOORSCHRIFT';
  return 'NIEUW';
}

function formatEuroFromCents(cents: number): string {
  return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('nl-BE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function isTimelineIndicatorType(messageType: string | undefined): boolean {
  return messageType === 'billing_notice' || messageType === 'closure_requested' || messageType === 'closure_rejected';
}

interface ConversationThreadProps {
  conversationId?: string;
  embedded?: boolean;
}

export interface ConversationThreadHandle {
  appendText: (text: string) => void;
  copyConversationToClipboard: () => Promise<boolean>;
}

export const ConversationThread = forwardRef<ConversationThreadHandle, ConversationThreadProps>(function ConversationThread({ conversationId, embedded = false }: ConversationThreadProps = {}, ref) {
  const params         = useParams<{ id: string; orgSlug?: string }>();
  const [searchParams] = useSearchParams();
  const id             = conversationId ?? params.id;
  const navigate       = useNavigate();
  const authUser       = useAuthUser();
  const authFetch      = useAuthFetch();
  const queryClient    = useQueryClient();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesRef    = useRef<HTMLDivElement>(null);
  const fileInputRef   = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const messageFieldRef = useRef<HTMLTextAreaElement>(null);
  const mollieProcessingStorageKey = id ? `mollie-processing:${id}` : null;

  const [message, setMessage]                       = useState('');
  const [attachMenuOpen, setAttachMenuOpen]         = useState(false);
  const attachMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const field = messageFieldRef.current;
    if (!field) return;
    field.style.height = 'auto';
    field.style.height = `${Math.min(field.scrollHeight, COMPOSE_MAX_HEIGHT)}px`;
  }, [message]);

  useEffect(() => {
    if (!attachMenuOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (attachMenuRef.current && !attachMenuRef.current.contains(e.target as Node)) {
        setAttachMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [attachMenuOpen]);

  const [pendingFiles, setPendingFiles]             = useState<File[]>([]);
  const [pendingFileUrls, setPendingFileUrls]       = useState<string[]>([]);
  const [unpreviewable, setUnpreviewable]           = useState<Set<number>>(new Set());
  const [optimisticMessage, setOptimisticMessage]   = useState<{ text: string; imageUrls: string[] } | null>(null);
  const [lightboxUrl, setLightboxUrl]               = useState<string | null>(null);
  const [lightboxZoom, setLightboxZoom]             = useState(1);
  const [lightboxPan, setLightboxPan]               = useState({ x: 0, y: 0 });
  const [isPanningLightbox, setIsPanningLightbox]   = useState(false);
  const lightboxDragRef = useRef<{ startX: number; startY: number; panX: number; panY: number } | null>(null);
  const [mobilePatientTab, setMobilePatientTab]     = useState<'consult' | 'extra'>('consult');
  const [feedbackRating, setFeedbackRating]         = useState(0);
  const [feedbackHover, setFeedbackHover]           = useState(0);
  const [feedbackText, setFeedbackText]             = useState('');
  const [feedbackSubmitted, setFeedbackSubmitted]   = useState(false);
  const [showMollieProcessing, setShowMollieProcessing] = useState(() => {
    const fromSource = searchParams.get('source') === 'mollie';
    if (!mollieProcessingStorageKey) return fromSource;
    try {
      const fromStorage = window.sessionStorage.getItem(mollieProcessingStorageKey) === '1';
      return fromSource || fromStorage;
    } catch {
      return fromSource;
    }
  });

  function setMollieProcessingFlag(active: boolean) {
    if (!mollieProcessingStorageKey) return;
    try {
      if (active) {
        window.sessionStorage.setItem(mollieProcessingStorageKey, '1');
      } else {
        window.sessionStorage.removeItem(mollieProcessingStorageKey);
      }
    } catch {
      // Ignore storage issues in restricted/private contexts.
    }
  }

  const scrollToLatestMessage = useCallback(function scrollToLatestMessage() {
    const end = messagesEndRef.current;
    if (!end) return;
    if (embedded) {
      const scrollHost = end.closest(`.${styles.workspaceScrollHost}`);
      if (scrollHost instanceof HTMLElement) {
        scrollHost.scrollTop = scrollHost.scrollHeight;
        return;
      }
    }
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
      return;
    }
    end.scrollIntoView({ behavior: 'instant' });
  }, [embedded]);

  // ── Data fetching ──────────────────────────────────────────
  const { data: conv, isPending, isError } = useQuery({
    queryKey: ['conversation', id],
    queryFn: async () => {
      const res = await authFetch(`/conversations/${id}`);
      if (!res.ok) throw new Error();
      return res.json() as Promise<ConversationDetail>;
    },
    refetchInterval: showMollieProcessing ? 2_000 : 10_000,
  });
  // Patients see the consultation in its practice's branding; a new consult starts at that practice.
  const { data: practice } = usePublicOrganization(conv?.doctor_organisation_slug);

  useImperativeHandle(ref, () => ({
    appendText: (text: string) => setMessage(prev => prev ? `${prev}\n\n${text}` : text),
    copyConversationToClipboard: () => {
      if (!conv) return Promise.resolve(false);
      const transcript = conv.messages
        .filter((msg) => !isTimelineIndicatorType(msg.message_type))
        .map((msg) => {
          const isDoctorMessage = msg.sender_cognito_sub === conv.doctor_cognito_sub;
          const senderLabel = isDoctorMessage ? doctorDisplayShortName(conv.doctor_name) : msg.sender_name;
          const attachmentNote = msg.attachments?.length ? ` [${msg.attachments.length} bijlage(n)]` : '';
          const body = msg.body ? richTextToPlainText(msg.body) : '';
          return `${senderLabel} — ${formatDateTime(msg.created_at)}\n${body}${attachmentNote}`;
        })
        .join('\n\n');
      return navigator.clipboard.writeText(transcript).then(() => true).catch(() => false);
    },
  }));

  // ── Mutations ──────────────────────────────────────────────
  const sendMutation = useMutation({
    mutationFn: async ({ text, files }: { text: string; files: File[] }) => {
      const imageFiles: ImageFile[] = [];

      // Step 1: upload each pending file directly to S3 via a presigned POST
      // (size-capped server-side, unlike a bare presigned PUT)
      for (const file of files) {
        const urlRes = await authFetch(`/conversations/${id}/upload-url`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ filename: file.name, mime_type: file.type }),
        });
        if (!urlRes.ok) throw new Error();
        const { url, fields, s3_key }: UploadUrlResponse = await urlRes.json();

        // POST directly to S3 — do NOT add Authorization header (presigned fields have their own auth)
        const formData = new FormData();
        for (const [k, v] of Object.entries(fields)) formData.append(k, v);
        formData.append('file', file);

        const postRes = await fetch(url, { method: 'POST', body: formData });
        if (!postRes.ok) throw new Error();
        imageFiles.push({ s3_key, mime_type: file.type });
      }

      // Step 2: send the message with the validated MIME type alongside each key
      const payload: SendMessageRequest = {
        body:        text.trim() || undefined,
        image_files: imageFiles.length ? imageFiles : undefined,
      } as SendMessageRequest;
      const res = await authFetch(`/conversations/${id}/messages`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['doctor-conversations'] });
      const previous = queryClient.getQueryData<ConversationSummary[]>(['doctor-conversations']);
      const now = new Date().toISOString();
      queryClient.setQueryData<ConversationSummary[]>(['doctor-conversations'], (old) =>
        old?.map((conv) =>
          conv.id === Number(id)
            ? { ...conv, last_message_sender_cognito_sub: authUser?.userId ?? '', last_message_at: now, updated_at: now }
            : conv
        ) ?? []
      );
      return { previous };
    },
    onError: (_err, vars, context) => {
      if (context?.previous) queryClient.setQueryData(['doctor-conversations'], context.previous);
      setOptimisticMessage(null);
      setMessage(vars.text);
      setPendingFiles(vars.files);
    },
    onSuccess: async () => {
      // Keep the optimistic bubble up until the real message has landed, so
      // the sent message never visibly disappears before it reappears.
      await queryClient.invalidateQueries({ queryKey: ['conversation', id] });
      queryClient.invalidateQueries({ queryKey: ['doctor-conversations'] });
      setOptimisticMessage(null);
    },
  });

  const confirmResolutionMutation = useMutation({
    mutationFn: () =>
      authFetch(`/conversations/${id}/confirm-resolution`, { method: 'POST' })
        .then((r) => { if (!r.ok) throw new Error(); }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation', id] });
      queryClient.invalidateQueries({ queryKey: ['patient-home'] });
    },
  });

  const rejectResolutionMutation = useMutation({
    mutationFn: () =>
      authFetch(`/conversations/${id}/reject-resolution`, { method: 'POST' })
        .then((r) => { if (!r.ok) throw new Error(); }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation', id] });
    },
  });

  const feedbackMutation = useMutation({
    mutationFn: (data: { star_rating?: number; feedback_text?: string; skip?: boolean }) =>
      authFetch(`/conversations/${id}/feedback`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(data),
      }).then((r) => { if (!r.ok) throw new Error(); }),
    onSuccess: (_data, variables) => {
      if (!variables.skip) setFeedbackSubmitted(true);
      queryClient.invalidateQueries({ queryKey: ['conversation', id] });
    },
  });

  const paymentCheckoutMutation = useMutation({
    mutationFn: async () => {
      const res = await authFetch(`/conversations/${id}/payments/create-checkout`, { method: 'POST' });
      if (!res.ok) {
        if (res.status === 409) {
          setMollieProcessingFlag(true);
          setShowMollieProcessing(true);
          void queryClient.invalidateQueries({ queryKey: ['conversation', id] });
          return null;
        }
        let message = 'Kon de betaling niet starten. Probeer opnieuw.';
        try {
          const body = (await res.json()) as { message?: string };
          if (typeof body.message === 'string' && body.message.trim()) message = body.message;
        } catch {
          // Keep fallback message when the response body cannot be parsed.
        }
        throw new Error(message);
      }
      const body = (await res.json()) as CreatePaymentCheckoutResponse;
      return body.checkout_url;
    },
    onSuccess: (checkoutUrl) => {
      if (!checkoutUrl) return;
      window.location.assign(checkoutUrl);
    },
  });

  const exportMutation = useMutation({
    mutationFn: async () => {
      const res = await authFetch(`/conversations/${id}/export`, { method: 'POST' });
      if (!res.ok) throw new Error('Kon de PDF niet genereren. Probeer opnieuw.');
      const body = (await res.json()) as ExportConversationResponse;
      return body.url;
    },
    // Optimistic and permanent by design — this is a reminder aid, not a strict
    // download receipt, so it ticks on click and doesn't revert on failure.
    onMutate: () => {
      queryClient.setQueryData<ConversationSummary[]>(['doctor-conversations'], (old) =>
        old?.map((c) => c.id === Number(id) ? { ...c, pdf_download_pending: false } : c) ?? []
      );
    },
    onSuccess: (url) => {
      triggerDownload(url);
    },
  });

  // ── Object URL lifecycle ──────────────────────────────────
  // Create fresh object URLs whenever pendingFiles changes and revoke the
  // previous set on cleanup — prevents blob URL leaks.
  useEffect(() => {
    const urls = pendingFiles.map((f) => URL.createObjectURL(f));
    setPendingFileUrls(urls);
    setUnpreviewable(new Set());
    return () => { urls.forEach((u) => URL.revokeObjectURL(u)); };
  }, [pendingFiles]);

  // Revoke the optimistic-bubble's own object URLs once it's replaced or cleared.
  useEffect(() => {
    return () => { optimisticMessage?.imageUrls.forEach((u) => URL.revokeObjectURL(u)); };
  }, [optimisticMessage]);

  // ── Lightbox keyboard dismiss ─────────────────────────────
  useEffect(() => {
    if (!lightboxUrl) return;
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setLightboxUrl(null); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxUrl]);

  // Reset zoom/pan whenever the lightbox opens on a (new) image.
  useEffect(() => {
    setLightboxZoom(1);
    setLightboxPan({ x: 0, y: 0 });
  }, [lightboxUrl]);

  function adjustLightboxZoom(delta: number) {
    setLightboxZoom((z) => {
      const next = Math.min(5, Math.max(1, +(z + delta).toFixed(2)));
      if (next === 1) setLightboxPan({ x: 0, y: 0 });
      return next;
    });
  }

  function handleLightboxWheel(e: React.WheelEvent<HTMLImageElement>) {
    e.preventDefault();
    adjustLightboxZoom(-e.deltaY * 0.0025);
  }

  function handleLightboxDoubleClick(e: React.MouseEvent<HTMLImageElement>) {
    e.stopPropagation();
    setLightboxZoom((z) => {
      const next = z > 1 ? 1 : 2.5;
      if (next === 1) setLightboxPan({ x: 0, y: 0 });
      return next;
    });
  }

  function handleLightboxPointerDown(e: React.PointerEvent<HTMLImageElement>) {
    if (lightboxZoom <= 1) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setIsPanningLightbox(true);
    lightboxDragRef.current = { startX: e.clientX, startY: e.clientY, panX: lightboxPan.x, panY: lightboxPan.y };
  }

  function handleLightboxPointerMove(e: React.PointerEvent<HTMLImageElement>) {
    if (!lightboxDragRef.current) return;
    const drag = lightboxDragRef.current;
    setLightboxPan({ x: drag.panX + (e.clientX - drag.startX), y: drag.panY + (e.clientY - drag.startY) });
  }

  function handleLightboxPointerUp() {
    lightboxDragRef.current = null;
    setIsPanningLightbox(false);
  }

  // ── Scroll to latest message ──────────────────────────────
  useEffect(() => {
    scrollToLatestMessage();
  }, [conv?.messages, optimisticMessage, scrollToLatestMessage]);

  useEffect(() => {
    setMobilePatientTab('consult');
  }, [id]);

  useEffect(() => {
    const fromSource = searchParams.get('source') === 'mollie';
    let fromStorage = false;
    if (mollieProcessingStorageKey) {
      try {
        fromStorage = window.sessionStorage.getItem(mollieProcessingStorageKey) === '1';
      } catch {
        fromStorage = false;
      }
    }
    if (fromSource) setMollieProcessingFlag(true);
    setShowMollieProcessing(fromSource || fromStorage);
  }, [searchParams, mollieProcessingStorageKey]);

  // ── File handling ─────────────────────────────────────────
  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    // Reset input so the same file can be re-selected after removal
    e.target.value = '';

    // Chrome/Firefox on Windows and Android report an empty file.type for
    // HEIC/HEIF since those OSes have no registered MIME handler for it —
    // fall back to the extension so these files aren't silently dropped.
    const normalized = files.map((f) => {
      if (f.type) return f;
      const ext = f.name.split('.').pop()?.toLowerCase();
      const mimeType = ext === 'heic' ? 'image/heic' : ext === 'heif' ? 'image/heif' : f.type;
      return mimeType === f.type ? f : new File([f], f.name, { type: mimeType });
    });

    const valid = normalized.filter(
      (f) => (ALLOWED_IMAGE_TYPES as readonly string[]).includes(f.type) && f.size <= MAX_FILE_BYTES,
    );
    const converted = await Promise.all(valid.map(convertHeicIfNeeded));
    const resized = await Promise.all(converted.map((file) => resizeImageIfNeeded(file)));
    setPendingFiles((prev) => [...prev, ...resized].slice(0, MAX_FILES));
  }

  function removeFile(index: number) {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  }

  // ── Event handlers ────────────────────────────────────────
  function handleSend(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!message.trim() && pendingFiles.length === 0) return;
    const text = message;
    const files = pendingFiles;
    setOptimisticMessage({ text, imageUrls: files.map((f) => URL.createObjectURL(f)) });
    setMessage('');
    setPendingFiles([]);
    sendMutation.mutate({ text, files });
  }

  function applyBold() {
    const field = messageFieldRef.current;
    if (!field) return;
    wrapFieldSelection(field, '**');
    field.focus();
  }

  function applyLink() {
    const field = messageFieldRef.current;
    if (!field) return;
    const url = window.prompt('Naar welke URL moet de link verwijzen?', 'https://');
    if (!url) return;
    if (getFieldSelection(field)) {
      wrapFieldSelection(field, '[', `](${url})`);
    } else {
      insertTextIntoField(field, `[tekst](${url})`);
    }
    field.focus();
  }

  function applyBullet() {
    const field = messageFieldRef.current;
    if (!field) return;
    const { selectionStart, selectionEnd, value } = field;
    const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
    const lineEndSearch = value.indexOf('\n', selectionEnd);
    const lineEnd = lineEndSearch === -1 ? value.length : lineEndSearch;
    const block = value.slice(lineStart, lineEnd);
    const prefixed = block.split('\n').map((l) => (l.startsWith('- ') ? l : `- ${l}`)).join('\n');
    setFieldText(field, value.slice(0, lineStart) + prefixed + value.slice(lineEnd));
    field.focus();
  }

  const mySub            = authUser?.userId ?? '';
  const isDoctor         = conv ? conv.doctor_cognito_sub === mySub : false;

  // Patients view a consultation inside its practice: forward /conversations/:id (email links,
  // the Mollie return) and a wrong practice in the path to /praktijk/:slug/consulten/:id.
  const conversationOrgSlug = conv?.doctor_organisation_slug ?? null;
  useEffect(() => {
    if (embedded || isDoctor || !id || !conversationOrgSlug || params.orgSlug === conversationOrgSlug) return;
    navigate(practiceConversationPath(conversationOrgSlug, id) + window.location.search, { replace: true });
  }, [embedded, isDoctor, id, conversationOrgSlug, params.orgSlug, navigate]);
  const isPendingPayment   = conv?.status === 'pending_payment';
  const isPaymentRequested = conv?.status === 'payment_requested';
  const isPaymentPaid      = conv?.payment_status === 'paid';
  const isClosureRequested = conv?.status === 'closure_requested';
  const paymentProcessingAfterRedirect =
    showMollieProcessing
    && !isDoctor
    && isPaymentRequested
    && !isPaymentPaid;
  const canCompose =
    conv?.status === 'active'
    || conv?.status === 'resolution_rejected'
    || (isDoctor && conv?.status === 'payment_requested');
  const lastRealMessage = conv?.messages.filter((m) => !isTimelineIndicatorType(m.message_type)).at(-1);
  const awaitingDoctorReply = !!lastRealMessage && lastRealMessage.sender_cognito_sub !== conv?.doctor_cognito_sub;
  const isFlaggedInactive = !!conv?.flagged_inactive_at && conv.status !== 'closed';
  const doctorPortrait   = conv?.doctor_picture_url ?? null;
  const patientDisplayName = (conv?.subject_first_name || conv?.subject_last_name)
    ? `${conv.subject_first_name ?? ''} ${conv.subject_last_name ?? ''}`.trim()
    : `${conv?.patient_first_name ?? ''} ${conv?.patient_last_name ?? ''}`.trim();
  const displayPregnant = conv?.intake_pregnant === 'yes' ? 'Ja' : 'Nee';
  const patientSex = conv ? sexFromRRN(conv.subject_rrn ?? conv.patient_rrn ?? '') : null;
  const showPregnantBadge = isDoctor && patientSex !== 'male' && displayPregnant === 'Ja';
  const firstPatientMessageId = conv?.messages.find((m) => !isTimelineIndicatorType(m.message_type) && m.sender_cognito_sub !== conv.doctor_cognito_sub)?.id;
  const displayMedication = conv?.intake_medication?.trim() ? conv.intake_medication : 'geen';
  const displayAllergies = conv?.intake_allergies?.trim() ? conv.intake_allergies : 'geen';
  const patientBirthIso = conv ? (birthDateFromRRN(conv.subject_rrn ?? conv.patient_rrn ?? '') ?? conv.patient_birth_date ?? null) : null;
  const patientAge = patientBirthIso ? ageFromIso(patientBirthIso) : null;
  const patientSexLabel = patientSex === 'male' ? 'man' : patientSex === 'female' ? 'vrouw' : null;
  const patientMetaLine = [
    patientSexLabel,
    patientAge !== null ? `${patientAge} j` : null,
    patientSex !== 'male' && displayPregnant === 'Nee' ? 'niet zwanger' : null,
  ].filter((part): part is string => part !== null).join(' · ');
  const medirisHref = buildMedirisPatientSearchHref(
    conv?.subject_rrn ? conv.subject_first_name : conv?.patient_first_name,
    conv?.subject_rrn ? conv.subject_last_name : conv?.patient_last_name,
    patientBirthIso,
  );
  const isFollowUpConsult = conv?.consult_type === 'follow_up';
  const consultTabSuffix = conv ? consultKindTabSuffix(conv) : null;
  const showExtraInfoOnly = !isDoctor && mobilePatientTab === 'extra';
  const consultKindLabel = consultTabSuffix && consultTabSuffix.charAt(0) + consultTabSuffix.slice(1).toLowerCase();

  const extraInfoCard = conv && (
    <div className={styles.patientInfoCard}>
      <p className={styles.patientInfoKicker}>Extra info</p>
      <dl className={styles.extraInfoList}>
        <div className={styles.extraInfoRow}>
          <dt>Patiënt</dt>
          <dd>{patientDisplayName}</dd>
        </div>
        <div className={styles.extraInfoRow}>
          <dt>Medicatie</dt>
          <dd>{displayMedication}</dd>
        </div>
        <div className={styles.extraInfoRow}>
          <dt>Allergieën</dt>
          <dd>{displayAllergies}</dd>
        </div>
        {patientSex !== 'male' && (
          <div className={styles.extraInfoRow}>
            <dt>Zwanger</dt>
            <dd>{displayPregnant}</dd>
          </div>
        )}
        {isFollowUpConsult && (
          <div className={styles.extraInfoRow}>
            <dt>Consulttype</dt>
            <dd>Opvolgconsultatie</dd>
          </div>
        )}
        <div className={styles.extraInfoRow}>
          <dt>Gestart op</dt>
          <dd>{new Date(conv.created_at).toLocaleDateString('nl-BE')}</dd>
        </div>
      </dl>
    </div>
  );

  const doctorAvatar = (className: string) => conv && (
    doctorPortrait
      ? <img src={doctorPortrait} alt="" className={`${styles.patientDoctorAvatar} ${className}`} />
      : <span className={`${styles.patientDoctorAvatar} ${className}`} aria-hidden="true">{getInitialsFromName(conv.doctor_name)}</span>
  );

  useEffect(() => {
    if (!showMollieProcessing || isDoctor) return;
    if (!conv) return;
    if (conv?.status !== 'payment_requested' || isPaymentPaid) {
      setShowMollieProcessing(false);
      setMollieProcessingFlag(false);
      return;
    }
  }, [showMollieProcessing, isDoctor, isPaymentPaid, conv]);

  const content = (
    <>
      {isPending && <p className="text-muted status-message">Laden&hellip;</p>}
      {isError   && <p className="text-muted status-message">Gesprek niet gevonden.</p>}

      {conv && (
        <>
          {isDoctor && (
            <div className={`${styles.segTabs} ${styles.doctorSegTabs} ${styles.mobileOnly}`} role="tablist" aria-label="Weergave wisselen">
              <button type="button" role="tab" aria-selected={mobilePatientTab === 'consult'} className={`${styles.segTab}${mobilePatientTab === 'consult' ? ` ${styles.segTabActive}` : ''}`} onClick={() => setMobilePatientTab('consult')}>Consult</button>
              <button type="button" role="tab" aria-selected={mobilePatientTab === 'extra'} className={`${styles.segTab}${mobilePatientTab === 'extra' ? ` ${styles.segTabActive}` : ''}`} onClick={() => setMobilePatientTab('extra')}>Patiënt</button>
            </div>
          )}
          {isDoctor && (
            <div className={`${styles.compactHeaderCard}${mobilePatientTab === 'consult' ? ` ${styles.mobileHidden}` : ''}`}>
              <span className={styles.cardTab}>Patient</span>
              <div className={styles.headerTop}>
                <div className={styles.compactPatientCol}>
                  <span className={styles.patientAvatar} aria-hidden="true">
                    {getInitialsFromName(patientDisplayName || 'Onbekend')}
                  </span>
                  <div className={styles.compactPatientBody}>
                    <h1 className={styles.compactName}>{patientDisplayName || 'Onbekend'}</h1>
                    {patientMetaLine && <p className={styles.headerMetaLine}>{patientMetaLine}</p>}
                    {showPregnantBadge && <span className={styles.pregnantBadge}>Zwanger</span>}
                  </div>
                </div>

                <div className={styles.headerActions}>
                  {medirisHref && (
                    <a
                      href={medirisHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.headerActionBtn}
                      title="Openen in Mediris"
                    >
                      <img src={mediportalUrl} alt="" width="16" height="11" />
                      Mediris
                    </a>
                  )}
                  <span className={styles.headerDate}>
                    {new Date(conv.created_at).toLocaleDateString('nl-BE', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                  {!embedded && isDoctor && conv.status === 'closed' && (
                    <button
                      type="button"
                      className={styles.headerActionBtn}
                      onClick={() => exportMutation.mutate()}
                      disabled={exportMutation.isPending}
                      title={exportMutation.isError ? 'Kon de PDF niet genereren. Probeer opnieuw.' : 'Download als PDF'}
                    >
                      <DownloadIcon size={12} className={styles.stripIcon} />
                      {exportMutation.isPending
                        ? 'PDF wordt gemaakt…'
                        : exportMutation.isError
                          ? 'Mislukt, probeer opnieuw'
                          : 'Download als PDF'}
                    </button>
                  )}
                  {(isPendingPayment || isPaymentRequested) && !isDoctor && !paymentProcessingAfterRedirect ? (
                    <button
                      type="button"
                      className={styles.paymentPill}
                      onClick={() => paymentCheckoutMutation.mutate()}
                      title="Naar betaling"
                      disabled={paymentCheckoutMutation.isPending}
                    >
                      <StatusBadge status={conv.status} label={statusLabel(conv.status)} flaggedInactiveAt={conv.flagged_inactive_at} />
                    </button>
                  ) : (
                    <StatusBadge status={conv.status} label={statusLabel(conv.status)} flaggedInactiveAt={conv.flagged_inactive_at} />
                  )}
                </div>
              </div>

              <div className={styles.headerInfoGrid}>
                {(conv.subject_rrn || conv.patient_rrn) && (
                  <div className={`${styles.infoCell} ${styles.infoCellCompact}`}>
                    <span className={styles.stripLabel}>Rijksregister</span>
                    {conv.subject_rrn ? (
                      <RrnDisplay
                        rrn={conv.subject_rrn}
                        firstName={conv.subject_first_name}
                        lastName={conv.subject_last_name}
                        birthDate={isDoctor ? birthDateFromRRN(conv.subject_rrn) : null}
                        showMedirisLink={false}
                      />
                    ) : (
                      <RrnDisplay
                        rrn={conv.patient_rrn!}
                        firstName={conv.patient_first_name}
                        lastName={conv.patient_last_name}
                        birthDate={isDoctor ? conv.patient_birth_date : null}
                        showMedirisLink={false}
                      />
                    )}
                  </div>
                )}
                {conv.patient_email && (
                  <div className={`${styles.infoCell} ${styles.infoCellCompact}`}>
                    <span className={styles.stripLabel}>E-mail</span>
                    <EmailDisplay email={conv.patient_email} />
                  </div>
                )}
                <div className={styles.infoCell} title={conv.intake_medication?.trim() || undefined}>
                  <span className={styles.stripLabel}>Medicatie</span>
                  <span className={styles.stripValue}>{displayMedication}</span>
                </div>
                <div className={styles.infoCell} title={conv.intake_allergies?.trim() || undefined}>
                  <span className={styles.stripLabel}>Allergieën</span>
                  <span className={styles.stripValue}>{displayAllergies}</span>
                </div>
              </div>
              {conv.subject_rrn && (
                <div className={styles.indienerFooter}>
                  <span className={styles.indienerAvatarSm}>
                    {getInitialsFromName(`${conv.patient_first_name ?? ''} ${conv.patient_last_name ?? ''}`.trim())}
                  </span>
                  <span className={styles.indienerFooterLabel}>Ingediend door</span>
                  <span className={styles.indienerFooterName}>{`${conv.patient_first_name ?? ''} ${conv.patient_last_name ?? ''}`.trim()}</span>
                </div>
              )}
            </div>
          )}

          <div className={isDoctor ? styles.layoutContents : styles.patientLayout}>
          <div className={isDoctor ? styles.layoutContents : styles.patientMain}>
          <div className={`${styles.threadCard}${!isDoctor ? ` ${styles.threadCardPatient}` : ''}${isDoctor && mobilePatientTab === 'extra' ? ` ${styles.mobileHidden}` : ''}`}>
            {isDoctor && (
              <span
                className={`${styles.cardTab}${consultTabSuffix != null ? ` ${styles.cardTabWithSuffix}` : ''}`}
              >
                <span className={styles.cardTabTitle}>Consult</span>
                {consultTabSuffix != null && (
                  <>
                    <span className={styles.cardTabDivider} aria-hidden="true" />
                    <span className={styles.cardTabKind}>{consultTabSuffix}</span>
                  </>
                )}
              </span>
            )}
            {!isDoctor && (
              <div className={styles.patientThreadHead}>
                <div className={styles.patientThreadHeadRow}>
                  <button
                    type="button"
                    className={styles.patientBackBtn}
                    aria-label="Terug naar consulten"
                    onClick={() => navigate(conv.doctor_organisation_slug ? practicePath(conv.doctor_organisation_slug) : ROUTES.practices)}
                  >
                    <ChevronLeftIcon size={20} />
                  </button>
                  {doctorAvatar(styles.patientDoctorAvatarSm)}
                  <div className={styles.patientThreadHeadText}>
                    <h1 className={styles.patientThreadTitle}>
                      <span className={styles.patientTitleNarrow}>{conv.doctor_name}</span>
                      <span className={styles.patientTitleWide}>Consult · {consultKindLabel}</span>
                    </h1>
                    {conv.subject_rrn && <p className={styles.patientThreadCaption}>Voor {patientDisplayName}</p>}
                  </div>
                  <StatusBadge status={conv.status} label={statusLabel(conv.status)} flaggedInactiveAt={conv.flagged_inactive_at} />
                </div>
                <div className={styles.segTabs} role="tablist" aria-label="Consultatie weergave">
                  <button type="button" role="tab" aria-selected={mobilePatientTab === 'consult'} className={`${styles.segTab}${mobilePatientTab === 'consult' ? ` ${styles.segTabActive}` : ''}`} onClick={() => setMobilePatientTab('consult')}>Consult</button>
                  <button type="button" role="tab" aria-selected={mobilePatientTab === 'extra'} className={`${styles.segTab}${mobilePatientTab === 'extra' ? ` ${styles.segTabActive}` : ''}`} onClick={() => setMobilePatientTab('extra')}>Extra info</button>
                </div>
              </div>
            )}
            {showExtraInfoOnly ? (
              <div className={styles.mobileExtraInfoPanel}>
                {extraInfoCard}
              </div>
            ) : (
              <>
                <div ref={messagesRef} className={`${styles.messages} ${embedded ? styles.workspaceScrollHost : ''}`}>
                  {(() => {
                    const items: React.ReactNode[] = [];
                    let lastDateKey = '';
                    for (const msg of conv.messages) {
                      const dateKey = new Date(msg.created_at).toDateString();
                      if (dateKey !== lastDateKey) {
                        lastDateKey = dateKey;
                        items.push(
                          <div key={`splitter-${dateKey}`} className={styles.dateSplitter}>
                            <div className={styles.dateSplitterLine} />
                            <span className={styles.dateSplitterLabel}>{formatDateSplitter(msg.created_at)}</span>
                            <div className={styles.dateSplitterLine} />
                          </div>
                        );
                      }
                      if (isTimelineIndicatorType(msg.message_type)) {
                        items.push(
                          <div key={msg.id} className={styles.timelineIndicatorRow}>
                            <div className={styles.timelineIndicator}>
                              <span className={styles.timelineIndicatorIconBadge} aria-hidden="true">
                                <InfoCircleIcon size={14} />
                              </span>
                              <p className={styles.timelineIndicatorText}>{msg.body}</p>
                            </div>
                            <time className={styles.timelineIndicatorTime} dateTime={msg.created_at}>
                              {formatTime(msg.created_at)}
                            </time>
                          </div>
                        );
                        continue;
                      }
                      const isDoctorMessage = msg.sender_cognito_sub === conv.doctor_cognito_sub;
                      const senderLabel = isDoctorMessage ? doctorDisplayShortName(conv.doctor_name) : msg.sender_name;
                      items.push(
                        <div
                          key={msg.id}
                          className={`${styles.msgRow} ${isDoctorMessage ? styles.msgRowReceived : styles.msgRowSent}`}
                        >
                          <div className={styles.msgContentRow}>
                            {isDoctorMessage && (
                              <span className={styles.msgAvatar} aria-hidden="true">
                                {doctorPortrait ? (
                                  <img src={doctorPortrait} alt="" className={styles.msgAvatarImage} />
                                ) : (
                                  getInitialsFromName(conv.doctor_name)
                                )}
                              </span>
                            )}
                            <div className={styles.msgBubbleCol}>
                              <div className={`${styles.bubble} ${isDoctorMessage ? styles.bubbleReceived : styles.bubbleSent}`}>
                                {msg.body && renderRichText(msg.body, styles.bubbleLink, styles.bubbleList)}
                                {msg.attachments?.map((att) => (
                                  <AuthImage
                                    key={att.url}
                                    src={att.url}
                                    alt={att.original_name}
                                    className={`${styles.attachmentImg} ${msg.body ? styles.attachmentImgWithBody : ''}`}
                                    onLoad={scrollToLatestMessage}
                                    onClick={(blobUrl) => setLightboxUrl(blobUrl)}
                                  />
                                ))}
                              </div>
                              <div className={`${styles.bubbleMeta} ${isDoctorMessage ? '' : styles.bubbleMetaSent}`}>
                                {senderLabel} &middot; {formatTime(msg.created_at)}
                              </div>
                              {showPregnantBadge && msg.id === firstPatientMessageId && <span className={`${styles.pregnantBadge} ${styles.pregnantBadgeMsg}`}>Zwanger</span>}
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return items;
                  })()}
                  {optimisticMessage && (
                    <div className={`${styles.msgRow} ${isDoctor ? styles.msgRowReceived : styles.msgRowSent}`}>
                      <div className={styles.msgContentRow}>
                        {isDoctor && (
                          <span className={styles.msgAvatar} aria-hidden="true">
                            {doctorPortrait ? (
                              <img src={doctorPortrait} alt="" className={styles.msgAvatarImage} />
                            ) : (
                              getInitialsFromName(conv.doctor_name)
                            )}
                          </span>
                        )}
                        <div className={styles.msgBubbleCol}>
                          <div className={`${styles.bubble} ${isDoctor ? styles.bubbleReceived : styles.bubbleSent} ${styles.bubblePending}`}>
                            {optimisticMessage.text && renderRichText(optimisticMessage.text, styles.bubbleLink, styles.bubbleList)}
                            {optimisticMessage.imageUrls.map((url) => (
                              <img
                                key={url}
                                src={url}
                                alt=""
                                className={`${styles.attachmentImg} ${optimisticMessage.text ? styles.attachmentImgWithBody : ''}`}
                              />
                            ))}
                          </div>
                          <div className={`${styles.bubbleMeta} ${isDoctor ? '' : styles.bubbleMetaSent}`}>
                            <span className={styles.sendingSpinner} aria-hidden="true" />Verzenden&hellip;
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {paymentProcessingAfterRedirect && (
                    <div className={styles.paymentRequestedNotice}>
                      <span className={styles.paymentRequestedIcon}>…</span>
                      <div className={styles.paymentRequestedText}>
                        <strong className={styles.paymentRequestedTitle}>Betaling wordt verwerkt</strong>
                        <span className={styles.paymentRequestedSub}>We bevestigen je betaling. Dit duurt meestal enkele seconden.</span>
                      </div>
                    </div>
                  )}
                  {isPaymentRequested && !isDoctor && conv && !paymentProcessingAfterRedirect && (
                    <div className={`${styles.msgRow} ${styles.msgRowReceived}`}>
                      <div className={styles.msgContentRow}>
                        <span className={styles.msgAvatar} aria-hidden="true">
                          {doctorPortrait ? <img src={doctorPortrait} alt="" className={styles.msgAvatarImage} /> : getInitialsFromName(conv.doctor_name)}
                        </span>
                        <div className={styles.msgBubbleCol}>
                          <div className={styles.newConsultCard}>
                            <div className={styles.newConsultHeader}>
                              <p className={styles.newConsultHeaderLabel}>Betaling Teleconsultatie:</p>
                            </div>
                            <div className={styles.newConsultAmountBox}>
                              <span className={styles.newConsultAmountLabel}>
                                {conv.payment_amount_cents === 500 ? 'Voorschriftenconsult' : 'Nieuwe teleconsultatie'}
                              </span>
                              <span className={styles.newConsultAmountValue}>{formatEuroFromCents(conv.payment_amount_cents)}</span>
                            </div>

                            <p className={styles.newConsultPayNote}>
                              Mogen we je vragen dit meteen in orde te brengen? Zo kunnen we je consult vlot afronden, alvast bedankt!
                            </p>

                            <button
                              type="button"
                              className={styles.newConsultPayBtn}
                              onClick={() => paymentCheckoutMutation.mutate()}
                              disabled={paymentCheckoutMutation.isPending || rejectResolutionMutation.isPending}
                            >
                              {paymentCheckoutMutation.isPending
                                ? 'Doorsturen...'
                                : `Online Betalen · ${formatEuroFromCents(conv.payment_amount_cents)}`}
                            </button>
                            <Button
                              variant="ghost"
                              onClick={() => rejectResolutionMutation.mutate()}
                              disabled={paymentCheckoutMutation.isPending || rejectResolutionMutation.isPending}
                            >
                              Nog vraag over dit probleem
                            </Button>
                            <div className={styles.newConsultPhysical}>
                              <p className={styles.newConsultSub}>
                                Info: Eventuele voorschriften kunnen pas opgemaakt worden zodra de betaling in orde is.
                              </p>
                            </div>

                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {!isDoctor && isClosureRequested && (
                    <div className={`${styles.msgRow} ${styles.msgRowReceived}`}>
                      <div className={styles.msgContentRow}>
                        <span className={styles.msgAvatar} aria-hidden="true">
                          {doctorPortrait ? <img src={doctorPortrait} alt="" className={styles.msgAvatarImage} /> : getInitialsFromName(conv.doctor_name)}
                        </span>
                        <div className={styles.msgBubbleCol}>
                          <div className={styles.closurePrompt}>
                            <div className={styles.closurePromptHeader}>
                              <CheckIcon size={14} className={styles.closurePromptIcon} />
                              <p className={styles.closurePromptTitle}>Ben je verder geholpen met {conv.doctor_name ?? 'de arts'}'s antwoord?</p>
                            </div>
                            <p className={styles.closurePromptSub}>Je kan het consult afronden of nog een extra vraag stellen.</p>
                            <div className={styles.closurePromptActions}>
                              <Button variant="primary" onClick={() => confirmResolutionMutation.mutate()} disabled={confirmResolutionMutation.isPending || rejectResolutionMutation.isPending}>Ja, afronden</Button>
                              <Button variant="ghost" onClick={() => rejectResolutionMutation.mutate()} disabled={confirmResolutionMutation.isPending || rejectResolutionMutation.isPending}>Nog vraag over dit probleem</Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {isPaymentPaid && (
                    <div className={styles.paymentRequestedNotice}>
                      <span className={styles.paymentRequestedIcon}>✓</span>
                      <div className={styles.paymentRequestedText}>
                        <strong className={styles.paymentRequestedTitle}>Betaling ontvangen</strong>
                        <span className={styles.paymentRequestedSub}>
                          Bedrag: {formatEuroFromCents(conv.payment_amount_cents)} · Ontvangen op {formatDateTime(conv.updated_at)}
                        </span>
                      </div>
                    </div>
                  )}
                  {!isDoctor && conv.status === 'closed' && (
                    <div className={styles.consultClosedInline}>
                      <span className={styles.consultClosedInlineCheck}><CheckIcon size={12} /></span>
                      <div className={styles.consultClosedInlineText}>
                        <strong className={styles.consultClosedInlineTitle}>Consult afgerond</strong>
                        <span className={styles.consultClosedInlineSub}>
                          {conv.export_emailed_at
                            ? 'U ontving een e-mail met het overzicht van dit consult. Nieuwe klacht? Start een nieuw teleconsult.'
                            : 'Nieuwe klacht? Start een nieuw teleconsult.'}
                        </span>
                      </div>
                      <Button variant="primary" onClick={() => navigate(conv.doctor_organisation_slug ? practiceIntakePath(conv.doctor_organisation_slug) : ROUTES.practices)}><span className={styles.nieuwBtnLabel}>Nieuw <ArrowRightIcon size={11} /></span></Button>
                    </div>
                  )}
                  {!isDoctor && conv.status === 'closed' && conv.feedback_pending && (
                    <div className={`${styles.msgRow} ${styles.msgRowReceived}`}>
                      <div className={styles.msgContentRow}>
                        <span className={styles.msgAvatar} aria-hidden="true">
                          {doctorPortrait ? <img src={doctorPortrait} alt="" className={styles.msgAvatarImage} /> : getInitialsFromName(conv.doctor_name)}
                        </span>
                        <div className={styles.msgBubbleCol}>
                          <div className={styles.feedbackPanel}>
                            <p className={styles.feedbackTitle}>Beoordeel dit consult</p>
                            <p className={styles.feedbackSub}>Hoe was je ervaring? Je feedback helpt ons Dermalink beter te maken.</p>
                            <div className={styles.feedbackStars} onMouseLeave={() => setFeedbackHover(0)}>
                              {[1, 2, 3, 4, 5].map((n) => {
                                const filled = (feedbackHover || feedbackRating) >= n;
                                return (
                                  <button key={n} type="button"
                                    className={`${styles.starBtn}${filled ? ` ${styles.starBtnFilled}` : ''}`}
                                    onClick={() => setFeedbackRating(n)}
                                    onMouseEnter={() => setFeedbackHover(n)}
                                    aria-label={`${n} ster`}
                                  >{filled ? '★' : '☆'}</button>
                                );
                              })}
                            </div>
                            <textarea className={`field-textarea ${styles.feedbackTextarea}`} value={feedbackText} onChange={(e) => setFeedbackText(e.target.value)} placeholder="Wat vond je goed? Wat kon beter? (optioneel)" />
                            {feedbackMutation.isError && <p className="text-muted">Kon feedback niet versturen.</p>}
                            <div className={styles.feedbackActions}>
                              <Button variant="primary" disabled={feedbackRating === 0 || feedbackMutation.isPending} onClick={() => feedbackMutation.mutate({ star_rating: feedbackRating, feedback_text: feedbackText.trim() || undefined })}>Verzenden</Button>
                              <Button variant="ghost" disabled={feedbackMutation.isPending} onClick={() => feedbackMutation.mutate({ skip: true })}>Sla over</Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {!isDoctor && conv.status === 'closed' && feedbackSubmitted && (
                    <div className={styles.feedbackThankYou}>
                      <div className={styles.feedbackThankYouStars} aria-hidden="true">
                        {[1, 2, 3, 4, 5].map((n) => <span key={n}>{n <= feedbackRating ? '★' : '☆'}</span>)}
                      </div>
                      <div>
                        <p className={styles.feedbackThankYouTitle}>Dank voor je feedback!</p>
                        <p className={styles.feedbackThankYouSub}>Je beoordeling helpt ons Dermalink te verbeteren.</p>
                      </div>
                    </div>
                  )}
                  {isDoctor && conv.status === 'closed' && (
                    <div className={styles.consultClosedInline}>
                      <span className={styles.consultClosedInlineCheck}><CheckIcon size={12} /></span>
                      <div className={styles.consultClosedInlineText}>
                        <strong className={styles.consultClosedInlineTitle}>Consult afgesloten</strong>
                        <span className={styles.consultClosedInlineSub}>Dit consult is afgesloten.</span>
                      </div>
                    </div>
                  )}
                  {isDoctor && conv.status === 'closed' && conv.star_rating !== null && (
                    <div className={styles.doctorFeedbackInline}>
                      <div className={styles.doctorFeedbackStars}>
                        {[1, 2, 3, 4, 5].map((n) => (
                          <span key={n} className={n <= conv.star_rating! ? styles.doctorFeedbackStarFilled : styles.doctorFeedbackStarEmpty}>★</span>
                        ))}
                      </div>
                      {conv.feedback_text && <p className={styles.doctorFeedbackText}>{conv.feedback_text}</p>}
                    </div>
                  )}
                  {isPaymentRequested && isDoctor && (
                    <div className={styles.paymentRequestedNotice}>
                      <span className={styles.paymentRequestedIcon}>€</span>
                      <div className={styles.paymentRequestedText}>
                        <strong className={styles.paymentRequestedTitle}>Betalingsverzoek verstuurd</strong>
                        <span className={styles.paymentRequestedSub}>Het consult wordt afgerond zodra de betaling bevestigd is.</span>
                      </div>
                    </div>
                  )}
                  {isClosureRequested && isDoctor && (
                    <div className={styles.paymentRequestedNotice}>
                      <span className={styles.paymentRequestedIcon}>✓</span>
                      <div className={styles.paymentRequestedText}>
                        <strong className={styles.paymentRequestedTitle}>Afsluitverzoek verstuurd</strong>
                        <span className={styles.paymentRequestedSub}>Het consult wordt afgesloten zodra de patiënt bevestigt.</span>
                      </div>
                    </div>
                  )}
                  {canCompose && isFlaggedInactive && (
                    <div className={styles.timelineIndicatorRow}>
                      <div className={styles.timelineIndicator}>
                        <span className={styles.timelineIndicatorIconBadge} aria-hidden="true">
                          <InfoCircleIcon size={14} />
                        </span>
                        <p className={styles.timelineIndicatorText}>
                          Dit gesprek is inactief. Zonder nieuw bericht wordt het automatisch afgesloten op {inactiveClosesOnLabel(conv.flagged_inactive_at!)}.
                        </p>
                      </div>
                    </div>
                  )}
                  {!isDoctor && canCompose && awaitingDoctorReply && (
                    <div className={styles.timelineIndicatorRow}>
                      <div className={styles.timelineIndicator}>
                        <span className={styles.timelineIndicatorIconBadge} aria-hidden="true">
                          <MailIcon size={18} />
                        </span>
                        <p className={styles.timelineIndicatorText}>
                          {doctorDisplayLastNameLabel(conv.doctor_name)} werd verwittigd van uw bericht en reageert zo snel mogelijk, ten laatste <strong>binnen 5 werkdagen</strong>. U krijgt een automatische <strong>e-mail</strong> als de dokter heeft geantwoord.
                        </p>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {canCompose && (
                  <form className={styles.compose} onSubmit={handleSend}>
                    {pendingFileUrls.length > 0 && (
                      <div className={styles.filePreviewList}>
                        {pendingFileUrls.map((url, i) => (
                          <div key={i} className={styles.filePreviewItem}>
                            {unpreviewable.has(i) ? (
                              <div className={styles.filePreviewFallback} title={pendingFiles[i]?.name}>
                                <FileDocumentIcon size={20} />
                                <span className={styles.filePreviewFallbackName}>{pendingFiles[i]?.name}</span>
                              </div>
                            ) : (
                              <img
                                src={url}
                                alt={pendingFiles[i]?.name}
                                className={styles.filePreviewThumb}
                                onError={() => setUnpreviewable((prev) => new Set(prev).add(i))}
                              />
                            )}
                            <button
                              type="button"
                              aria-label="Verwijder afbeelding"
                              onClick={() => removeFile(i)}
                              className={styles.fileRemoveBtn}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {isDoctor && (
                      <div className={styles.toolbar}>
                        <button type="button" className={styles.toolbarBtn} title="Vet" aria-label="Vet" onClick={applyBold} disabled={sendMutation.isPending}><BoldIcon size={14} /></button>
                        <button type="button" className={styles.toolbarBtn} title="Link invoegen" aria-label="Link invoegen" onClick={applyLink} disabled={sendMutation.isPending}><LinkIcon size={14} /></button>
                        <button type="button" className={styles.toolbarBtn} title="Opsomming" aria-label="Opsomming" onClick={applyBullet} disabled={sendMutation.isPending}><ListBulletIcon size={14} /></button>
                      </div>
                    )}
                    <div className={`${styles.composeInputRow} ${styles.composeBox}`}>
                      <div className={styles.attachMenuWrapper} ref={attachMenuRef}>
                        <Button
                          variant="ghost"
                          icon={<CameraIcon size={18} />}
                          className={styles.cameraSquareBtn}
                          onClick={() => setAttachMenuOpen((v) => !v)}
                          disabled={sendMutation.isPending || pendingFiles.length >= MAX_FILES}
                          aria-expanded={attachMenuOpen}
                          aria-haspopup="true"
                          title="Afbeelding toevoegen"
                          aria-label="Afbeelding toevoegen"
                        />
                        {attachMenuOpen && (
                          <div className={styles.attachMenu}>
                            <button
                              type="button"
                              className={`${styles.attachMenuItem} ${styles.mobileOnly}`}
                              onClick={() => { setAttachMenuOpen(false); fileInputRef.current?.click(); }}
                            >
                              <CameraIcon size={16} />
                              Foto maken
                            </button>
                            <button
                              type="button"
                              className={styles.attachMenuItem}
                              onClick={() => { setAttachMenuOpen(false); galleryInputRef.current?.click(); }}
                            >
                              <UploadIcon size={16} />
                              Afbeelding kiezen
                            </button>
                          </div>
                        )}
                      </div>
                      <textarea
                        ref={messageFieldRef}
                        className={`field-textarea ${styles.textarea}`}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Schrijf een bericht…"
                        disabled={sendMutation.isPending}
                        maxLength={2000}
                        rows={1}
                      />
                      <Button
                        type="submit"
                        variant="primary"
                        icon={<SendIcon size={18} />}
                        className={styles.sendCircleBtn}
                        isLoading={sendMutation.isPending}
                        disabled={!message.trim() && pendingFiles.length === 0}
                        title="Verzenden"
                        aria-label="Verzenden"
                      />
                    </div>
                    {!isDoctor && (
                      <div className={styles.composeHintSingleProblemRow}>
                        <span className={styles.composeHintInfoIcon} aria-hidden="true">i</span>
                        <p className={styles.composeHintSingleProblem}>
                          Uw dermatoloog beoordeelt één probleem per consult.
                        </p>
                      </div>
                    )}
                    {message.length >= 1800 && <p className={styles.charCounter}>{message.length}/2000</p>}
                    {sendMutation.isError && <p className="text-muted">Bericht kon niet worden verzonden.</p>}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      multiple
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                    />
                    <input
                      ref={galleryInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                    />
                  </form>
                )}

                {!canCompose && (isDoctor || conv.status !== 'closed') && (
                  <div className={styles.backBtnRow} />
                )}
              </>
            )}
          </div>
          </div>
          {!isDoctor && (
            <aside className={styles.patientAside}>
              <div className={`${styles.patientInfoCard} ${styles.patientDoctorCard}`}>
                {doctorAvatar(styles.patientDoctorAvatarLg)}
                <p className={styles.patientInfoKicker}>Jouw dermatoloog</p>
                <p className={styles.patientDoctorCardName}>{conv.doctor_name}</p>
                {conv.doctor_organisation && <p className={styles.patientDoctorCardOrg}>{conv.doctor_organisation}</p>}
              </div>
              {extraInfoCard}
            </aside>
          )}
          </div>
        </>
      )}

      {lightboxUrl && (
        <div className={styles.lightbox} onClick={() => setLightboxUrl(null)}>
          <img
            src={lightboxUrl}
            alt=""
            className={styles.lightboxImg}
            style={{
              transform: `translate(${lightboxPan.x}px, ${lightboxPan.y}px) scale(${lightboxZoom})`,
              transition: isPanningLightbox ? 'none' : 'transform 0.15s ease-out',
              cursor: lightboxZoom > 1 ? (isPanningLightbox ? 'grabbing' : 'grab') : 'zoom-in',
            }}
            draggable={false}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={handleLightboxDoubleClick}
            onWheel={handleLightboxWheel}
            onPointerDown={handleLightboxPointerDown}
            onPointerMove={handleLightboxPointerMove}
            onPointerUp={handleLightboxPointerUp}
            onPointerLeave={handleLightboxPointerUp}
          />
          <div className={styles.lightboxControls}>
            <Button variant="ghost" onClick={(e) => { e.stopPropagation(); adjustLightboxZoom(-0.5); }} disabled={lightboxZoom <= 1} aria-label="Uitzoomen">−</Button>
            <Button variant="ghost" onClick={(e) => { e.stopPropagation(); adjustLightboxZoom(0.5); }} disabled={lightboxZoom >= 5} aria-label="Inzoomen">+</Button>
            <Button variant="ghost" onClick={() => setLightboxUrl(null)}>Sluiten</Button>
          </div>
        </div>
      )}
    </>
  );

  if (embedded) return <div className={styles.embeddedRoot}>{content}</div>;

  return (
    <AuthPageShell maxWidth={isDoctor ? '800px' : '1120px'} padTop="0" flexMain hideFooter patientBackdrop={!isDoctor} practice={isDoctor ? null : practice}>
      {content}
    </AuthPageShell>
  );
});
